import { Navigate, Outlet, useLocation } from 'react-router';

import { Container } from '@/components/ui/Container';
import { LOCKED_SUBSCRIBE_PATH, openWhileLocked } from '@/features/billing/lib/billingLock';

import { useAuth } from '../hooks/useAuth';

/**
 * Route guard.
 *
 * This is convenience and UX, not security: the backend authorises every
 * request independently. Hiding a page the API would refuse anyway just avoids
 * showing people doors that do not open.
 */
export function ProtectedRoute({
  requireBusiness = true,
  permission,
  platformOwnerOnly = false,
}: {
  requireBusiness?: boolean;
  permission?: string;
  /** Only the platform owner (`SUPER_ADMIN`), whatever else the caller may hold. */
  platformOwnerOnly?: boolean;
}) {
  const {
    status,
    hasBusiness,
    isPlatformStaff,
    isPartner,
    isPlatformOwner,
    mustChangePassword,
    billingLock,
    can,
  } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="grid min-h-dvh place-items-center">
        <p className="text-sm text-muted">Loading your workspace…</p>
      </div>
    );
  }

  if (status === 'anonymous') {
    // The query string comes along: a payment link (`/subscribe?plan=ENT_ACME`)
    // opened while signed out must land on that plan after signing in.
    return (
      <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
    );
  }

  // Checked before the business and permission gates, because it outranks both:
  // the API refuses everything while it holds, so onboarding or a permission
  // wall would be the wrong explanation for what the caller is seeing.
  if (mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  // A lapsed plan nobody renewed: the store is closed and the API refuses
  // everything but billing, so the merchant is sent to pay. Before the
  // business and permission gates because it outranks them too, and before
  // `RequireSubscription` (a child of this guard) so the two cannot disagree
  // about where an unsettled plan goes - that guard would also send them to
  // `/subscribe`, only without the lock banner.
  if (billingLock && !openWhileLocked(location.pathname)) {
    return <Navigate to={LOCKED_SUBSCRIBE_PATH} replace />;
  }

  if (requireBusiness && !hasBusiness) {
    // Platform staff and partners have no business and never will. Sending
    // either through merchant onboarding would ask them to create one, which
    // is both wrong and - since neither account may own a tenant - impossible.
    if (isPlatformStaff) return <Navigate to="/platform" replace />;
    if (isPartner) return <Navigate to="/partner" replace />;
    return <Navigate to="/onboarding" replace />;
  }

  if ((permission && !can(permission)) || (platformOwnerOnly && !isPlatformOwner)) {
    return (
      <Container className="py-20 text-center">
        <h1 className="text-2xl font-bold text-ink">You do not have access to this page</h1>
        <p className="mt-2 text-muted">Ask an owner or admin of this business for access.</p>
      </Container>
    );
  }

  return <Outlet />;
}
